import { createContext, useContext, useReducer, useEffect, ReactNode } from 'react'
import { 
  AuthContextType, 
  AuthState, 
  LoginCredentials, 
  RegisterData, 
  User, 
  UserRole, 
  Permission,
  hasPermission,
  hasRole,
  hasAnyRole
} from '../types/auth'
import { realAuthService } from '../services/realAuthService'

type AuthAction = 
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'REGISTER_START' }
  | { type: 'REGISTER_SUCCESS'; payload: User }
  | { type: 'REGISTER_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'RESTORE_SESSION'; payload: User }

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
    case 'REGISTER_START':
      return {
        ...state,
        isLoading: true,
        error: null
      }
    
    case 'LOGIN_SUCCESS':
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null
      }
    
    case 'LOGIN_FAILURE':
    case 'REGISTER_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      }
    
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      }
    
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      }
    
    case 'RESTORE_SESSION':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null
      }
    
    default:
      return state
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState)
  
  // Real authentication service using D1 API
  
  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      dispatch({ type: 'LOGIN_START' })
      
      const result = await realAuthService.login(credentials)
      
      if (result.success) {
        // Save user to localStorage for session persistence
        localStorage.setItem('brandscope_user', JSON.stringify(result.data))
        localStorage.setItem('brandscope_session', Date.now().toString())
        
        dispatch({ type: 'LOGIN_SUCCESS', payload: result.data })
        return true
      } else {
        dispatch({ type: 'LOGIN_FAILURE', payload: result.error })
        return false
      }
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE', payload: error instanceof Error ? error.message : 'Login failed' })
      return false
    }
  }
  
  const register = async (data: RegisterData): Promise<boolean> => {
    try {
      dispatch({ type: 'REGISTER_START' })
      
      const result = await realAuthService.register(data)
      
      if (result.success) {
        // After registration, automatically log in
        const loginResult = await realAuthService.login({
          email: data.email,
          password: data.password
        })
        
        if (loginResult.success) {
          // Save user to localStorage for session persistence
          localStorage.setItem('brandscope_user', JSON.stringify(loginResult.data))
          localStorage.setItem('brandscope_session', Date.now().toString())
          
          dispatch({ type: 'REGISTER_SUCCESS', payload: loginResult.data })
          return true
        } else {
          dispatch({ type: 'REGISTER_FAILURE', payload: 'Registration successful but login failed' })
          return false
        }
      } else {
        dispatch({ type: 'REGISTER_FAILURE', payload: result.error })
        return false
      }
    } catch (error) {
      dispatch({ type: 'REGISTER_FAILURE', payload: error instanceof Error ? error.message : 'Registration failed' })
      return false
    }
  }
  
  const logout = async () => {
    try {
      await realAuthService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    }
    
    // Clear localStorage and state regardless of API response
    localStorage.removeItem('brandscope_user')
    localStorage.removeItem('brandscope_session')
    dispatch({ type: 'LOGOUT' })
  }
  
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }
  
  const hasPermissionCheck = (permission: Permission): boolean => {
    if (!state.user) return false
    return hasPermission(state.user.role, permission)
  }
  
  const hasRoleCheck = (role: UserRole): boolean => {
    if (!state.user) return false
    return hasRole(state.user.role, role)
  }
  
  const hasAnyRoleCheck = (roles: UserRole[]): boolean => {
    if (!state.user) return false
    return hasAnyRole(state.user.role, roles)
  }
  
  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      try {
        // Check if we have tokens and try to get current user
        if (realAuthService.isAuthenticated()) {
          const userResult = await realAuthService.getCurrentUser()
          
          if (userResult.success) {
            // Save user to localStorage for consistency
            localStorage.setItem('brandscope_user', JSON.stringify(userResult.data))
            localStorage.setItem('brandscope_session', Date.now().toString())
            
            dispatch({ type: 'RESTORE_SESSION', payload: userResult.data })
            return
          }
        }
        
        // If we have saved user but tokens are invalid, clear everything
        const savedUser = localStorage.getItem('brandscope_user')
        const savedSession = localStorage.getItem('brandscope_session')
        
        if (savedUser && savedSession) {
          const sessionTime = parseInt(savedSession, 10)
          const currentTime = Date.now()
          const sessionAge = currentTime - sessionTime
          
          // Session expires after 24 hours, but tokens might be shorter
          if (sessionAge < 24 * 60 * 60 * 1000) {
            // Try one more time to get user with stored data
            try {
              const user: User = JSON.parse(savedUser)
              dispatch({ type: 'RESTORE_SESSION', payload: user })
              return
            } catch (parseError) {
              console.error('Failed to parse saved user:', parseError)
            }
          }
        }
        
        // Clear expired or invalid session
        localStorage.removeItem('brandscope_user')
        localStorage.removeItem('brandscope_session')
      } catch (error) {
        console.error('Failed to restore session:', error)
        localStorage.removeItem('brandscope_user')
        localStorage.removeItem('brandscope_session')
      }
    }
    
    restoreSession()
  }, [])
  
  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    clearError,
    hasPermission: hasPermissionCheck,
    hasRole: hasRoleCheck,
    hasAnyRole: hasAnyRoleCheck
  }
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Higher-order component for protecting routes
interface ProtectedRouteProps {
  children: ReactNode
  requiredPermission?: Permission
  requiredRole?: UserRole
  requiredRoles?: UserRole[]
  fallback?: ReactNode
}

export function ProtectedRoute({ 
  children, 
  requiredPermission, 
  requiredRole, 
  requiredRoles,
  fallback 
}: ProtectedRouteProps) {
  const { isAuthenticated, hasPermission, hasRole, hasAnyRole } = useAuth()
  
  if (!isAuthenticated) {
    return fallback || <div className="p-4 text-center text-gray-500">Please log in to access this feature.</div>
  }
  
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return fallback || <div className="p-4 text-center text-gray-500">You don't have permission to access this feature.</div>
  }
  
  if (requiredRole && !hasRole(requiredRole)) {
    return fallback || <div className="p-4 text-center text-gray-500">You don't have the required role to access this feature.</div>
  }
  
  if (requiredRoles && !hasAnyRole(requiredRoles)) {
    return fallback || <div className="p-4 text-center text-gray-500">You don't have any of the required roles to access this feature.</div>
  }
  
  return <>{children}</>
}

// Hook for conditional rendering based on permissions
export function usePermission(permission: Permission): boolean {
  const { hasPermission } = useAuth()
  return hasPermission(permission)
}

export function useRole(role: UserRole): boolean {
  const { hasRole } = useAuth()
  return hasRole(role)
}

export function useAnyRole(roles: UserRole[]): boolean {
  const { hasAnyRole } = useAuth()
  return hasAnyRole(roles)
}